import * as THREE from "three";
import { useMemo } from "react";

// A plain kitchen island (34.5" base + toe kick), a stool and a 6 ft figure so the slab
// reads at true scale. All units in inches; the slab sits on top at 36" counter height.
export const BASE_H = 34.5;

const paint = new THREE.MeshStandardMaterial({
  color: "#3b3f47",
  roughness: 0.55,
});
const kick = new THREE.MeshStandardMaterial({
  color: "#23262b",
  roughness: 0.8,
});
const steel = new THREE.MeshStandardMaterial({
  color: "#9a9ea6",
  roughness: 0.35,
  metalness: 0.7,
});
const seat = new THREE.MeshStandardMaterial({
  color: "#c9a97a",
  roughness: 0.7,
});
const ghost = new THREE.MeshStandardMaterial({
  color: "#8d8f99",
  roughness: 0.9,
  transparent: true,
  opacity: 0.55,
});

const Stool = ({ x, z }) => (
  <group position={[x, 0, z]}>
    <mesh position-y={0.4} material={steel}>
      <cylinderGeometry args={[8, 8, 0.8, 32]} />
    </mesh>
    <mesh position-y={12.8} material={steel}>
      <cylinderGeometry args={[1.1, 1.1, 24, 16]} />
    </mesh>
    <mesh position-y={25.6} material={seat} castShadow>
      <cylinderGeometry args={[7, 7, 1.6, 32]} />
    </mesh>
  </group>
);

// 72" figure: legs, torso, arms, head
const Person = ({ x, z }) => (
  <group position={[x, 0, z]} rotation-y={Math.PI / 2}>
    {[-4, 4].map((dx) => (
      <mesh key={dx} position={[dx, 16, 0]} material={ghost} castShadow>
        <cylinderGeometry args={[3, 3.4, 32, 16]} />
      </mesh>
    ))}
    <mesh position={[0, 45, 0]} material={ghost} castShadow>
      <cylinderGeometry args={[6.5, 7.5, 26, 20]} />
    </mesh>
    {[-10.5, 10.5].map((dx) => (
      <mesh key={dx} position={[dx, 46, 0]} material={ghost} castShadow>
        <cylinderGeometry args={[2, 2.2, 24, 12]} />
      </mesh>
    ))}
    <mesh position={[0, 67, 0]} material={ghost} castShadow>
      <sphereGeometry args={[4.6, 24, 24]} />
    </mesh>
  </group>
);

export const Island = ({ L, Wd }) => {
  const fx = Math.max(24, L - 3); // 1.5" overhang each side
  const fz = Math.max(18, Wd - 3);
  const doors = useMemo(() => {
    const n = Math.max(1, Math.round(fx / 24));
    const w = fx / n;
    return Array.from({ length: n }, (_, i) => -fx / 2 + w * (i + 0.5));
  }, [fx]);
  return (
    <group>
      <mesh
        position-y={2 + (BASE_H - 4) / 2}
        material={paint}
        castShadow
        receiveShadow
      >
        <boxGeometry args={[fx, BASE_H - 4, fz]} />
      </mesh>
      <mesh position-y={2} material={kick}>
        <boxGeometry args={[fx - 4, 4, fz - 4]} />
      </mesh>
      {doors.map((x) => (
        <mesh
          key={x}
          position={[x, 2 + (BASE_H - 4) / 2, fz / 2 + 0.15]}
          material={kick}
        >
          <boxGeometry args={[fx / doors.length - 2, BASE_H - 8, 0.3]} />
        </mesh>
      ))}
      {doors.map((x) => (
        <mesh
          key={`h${x}`}
          position={[x, BASE_H - 6, fz / 2 + 0.6]}
          material={steel}
        >
          <boxGeometry args={[6, 0.6, 0.6]} />
        </mesh>
      ))}
      <Stool x={-fx / 4} z={-(fz / 2 + 12)} />
      <Stool x={fx / 4} z={-(fz / 2 + 12)} />
      <Person x={-(fx / 2 + 22)} z={0} />
    </group>
  );
};

export default Island;
